import { defaultsDeep } from 'lodash';

import {
  ProcessResponseDTO,
  ProcessResponseDTOSendModesItem,
  ProcessResponseDTOStatus,
  ProcessSettings,
  ProcessUpdateDto,
  ProcessUpdateDtoAccessModesItem,
  ProcessUpdateDtoAssessmentType,
  ProcessUpdateDtoDefaultLocale,
  ProcessUpdateDtoSendModesItem,
  TalentQuestionsResponseDTO,
  TestSettings
} from '@/api/generated';
import { DEFAULT_LANGUAGE_CODE } from '@/views/TalentRegistrationForm/constants';

import { AccessMethods, WebcamMode } from './SettingsTab/options';

export interface BasicInfoFormValues {
  name: string;
  assessmentType: ProcessUpdateDtoAssessmentType;
  description: string;
  availability: {
    start: string;
    end: string;
  };
  status: ProcessResponseDTOStatus;
}

export interface TalentRegistrationFormValues extends TalentQuestionsResponseDTO {}

export interface AccessInvitationFormValues {
  hasPublicLink?: boolean;
  talentInvitationLanguage: string;
  invitationChannels: string[];
  accessMethods: string[];
}

export interface TestPropertiesFormValues {
  enableClarificationQuestions: boolean;
  webcam: WebcamMode;
  proctoringAI: boolean;
  captureFrequency?: number;
}

export interface TalentFormValues {
  talentsToAdd?: string[];
}

const ONE_WEEK_MS = 7 * 24 * 3600 * 1000;

const DEFAULT_BASIC_INFO: BasicInfoFormValues = {
  name: '',
  assessmentType: ProcessUpdateDtoAssessmentType.EXTERNAL,
  description: '',
  availability: {
    start: new Date()?.toISOString(),
    end: new Date(Date.now() + ONE_WEEK_MS)?.toISOString()
  },
  status: ProcessResponseDTOStatus.UNPUBLISHED
};

const DEFAULT_TALENT_REGISTRATION: TalentRegistrationFormValues = {
  external: [],
  internal: [],
  translations: {
    [DEFAULT_LANGUAGE_CODE]: {}
  }
};

const DEFAULT_ACCESS_INVITATION: AccessInvitationFormValues = {
  hasPublicLink: false,
  talentInvitationLanguage: 'en-US',
  invitationChannels: [ProcessResponseDTOSendModesItem.EMAIL],
  accessMethods: [AccessMethods.Otp]
};

export const DEFAULT_TALENTS: TalentFormValues = {
  talentsToAdd: []
};

export function createBasicInfoDefaultValues(process?: ProcessResponseDTO): BasicInfoFormValues {
  return {
    name: process?.name || DEFAULT_BASIC_INFO.name,
    assessmentType: process?.assessmentType || DEFAULT_BASIC_INFO.assessmentType,
    description: process?.description || DEFAULT_BASIC_INFO.description,
    status: process?.status || DEFAULT_BASIC_INFO.status,
    availability: {
      start: process?.startDate || DEFAULT_BASIC_INFO.availability?.start,
      end: process?.endDate || DEFAULT_BASIC_INFO.availability?.end
    }
  };
}

export function createTalentRegistrationDefaultValues(
  values?: TalentRegistrationFormValues
): TalentRegistrationFormValues {
  return defaultsDeep(values || {}, DEFAULT_TALENT_REGISTRATION);
}

export function createAccessInvitationDefaultValues(process?: ProcessSettings): AccessInvitationFormValues {
  const otpAllowed = process?.accessModes?.includes(ProcessUpdateDtoAccessModesItem.PASSWORD);
  const isUniqueLink = process?.accessModes?.includes(ProcessUpdateDtoAccessModesItem.UNIQUE_LINK);
  const isPublicLink = process?.accessModes?.includes(ProcessUpdateDtoAccessModesItem.PUBLIC_LINK);

  const accessMethods =
    (otpAllowed || isUniqueLink || isPublicLink) &&
    [isUniqueLink || isPublicLink ? AccessMethods.Link : null, otpAllowed ? AccessMethods.Otp : null].filter(
      (item): item is AccessMethods => Boolean(item)
    );

  const defaultAccessMethods = [AccessMethods.Otp];

  return {
    accessMethods: accessMethods || defaultAccessMethods,
    hasPublicLink: isPublicLink || DEFAULT_ACCESS_INVITATION.hasPublicLink,
    invitationChannels: process?.sendModes || [],
    talentInvitationLanguage: process?.defaultLocale || DEFAULT_LANGUAGE_CODE
  };
}

export function createTestPropertiesDefaultValues(process?: ProcessSettings): TestPropertiesFormValues {
  const settings = process?.testSettings;
  let webcam = WebcamMode.Disabled;
  if (settings?.webcamSettings?.isEnabled) {
    webcam = settings?.webcamSettings?.isMandatory ? WebcamMode.Mandatory : WebcamMode.Optional;
  }

  return {
    enableClarificationQuestions: settings?.isClarificationQuestionsEnabled || false,
    webcam,
    proctoringAI: settings?.webcamSettings?.isAiAnalysisEnabled || false,
    captureFrequency: settings?.webcamSettings?.captureFrequencySeconds || 60
  };
}

export const accessInvitationMappingPresave = (values?: AccessInvitationFormValues) => {
  const linkAllowed = values?.accessMethods?.includes(AccessMethods.Link);
  const otpAllowed = values?.accessMethods?.includes(AccessMethods.Otp);
  const accessInvitationData: ProcessUpdateDto = {
    accessModes: [
      otpAllowed && ProcessUpdateDtoAccessModesItem.PASSWORD,
      linkAllowed && ProcessUpdateDtoAccessModesItem.UNIQUE_LINK,
      values?.hasPublicLink && ProcessUpdateDtoAccessModesItem.PUBLIC_LINK
    ].filter((item): item is ProcessUpdateDtoAccessModesItem => Boolean(item)),
    sendModes: values?.invitationChannels as ProcessUpdateDtoSendModesItem[],
    defaultLocale: values?.talentInvitationLanguage as ProcessUpdateDtoDefaultLocale
  };

  return accessInvitationData;
};

export const testSettingsMappingPresave = (values?: TestPropertiesFormValues) => {
  const testSettingsData: TestSettings = {
    isClarificationQuestionsEnabled: values?.enableClarificationQuestions,
    webcamSettings: {
      isEnabled: [WebcamMode.Mandatory, WebcamMode.Optional].includes(values?.webcam as WebcamMode),
      isMandatory: values?.webcam === WebcamMode.Mandatory,
      isAiAnalysisEnabled: values?.proctoringAI
    }
  };

  return { testSettings: testSettingsData };
};
